import type { ReactElement } from 'react';
import { Columns3, LayoutGrid, Columns2, Table2 } from 'lucide-react';
import { Tooltip, cn } from '@vd/ui';
import type { ViewMode } from '@vd/types';
import styles from './ViewToggle.module.css';

const views: Array<{ key: ViewMode; label: string; icon: ReactElement }> = [
  { key: 'board',  label: 'Board',  icon: <Columns3 size={14} /> },
  { key: 'cards',  label: 'Cards',  icon: <LayoutGrid size={14} /> },
  { key: 'single', label: 'Single', icon: <Columns2 size={14} /> },
  { key: 'table',  label: 'Table',  icon: <Table2 size={14} /> },
];

export interface ViewToggleProps {
  /**
   * Currently-active view, or `null` when the host page isn't itself a
   * view (e.g. the ticket detail page uses this control purely as a
   * navigator into the board). When `null`, no button is highlighted
   * and `aria-selected` is false on every button.
   */
  viewMode: ViewMode | null;
  onChange: (v: ViewMode) => void;
  className?: string;
  /**
   * Optional subset of modes to show. Defaults to all four (board /
   * cards / single / table). Pages without a kanban concept (e.g.
   * the Inbox views, which group by project) pass
   * `['cards', 'single', 'table']` to drop the board button.
   */
  modes?: ReadonlyArray<ViewMode>;
  /** The `data-tour-anchor` a tour points at this toggle by (TOADO-971), where one does. */
  tourAnchor?: string;
}

export function ViewToggle({ viewMode, onChange, className, modes, tourAnchor }: ViewToggleProps) {
  const shown = modes ? views.filter((v) => modes.includes(v.key)) : views;
  return (
    <div className={cn(styles.group, className)} role="tablist" aria-label="View mode" data-tour-anchor={tourAnchor}>
      {shown.map((v) => (
        <Tooltip key={v.key} content={`${v.label} · V`}>
          <button
            role="tab"
            aria-selected={viewMode === v.key}
            className={cn(styles.btn, viewMode === v.key && styles.active)}
            onClick={() => onChange(v.key)}
            // Each view by name too, for the tour that has Table tried (Jason, 28 Oct).
            data-tour-anchor={tourAnchor ? `${tourAnchor}.${v.key}` : undefined}
          >
            {v.icon}
            <span>{v.label}</span>
          </button>
        </Tooltip>
      ))}
    </div>
  );
}
